import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { useToast } from "@shared/hooks/use-toast";
import { addResource, editResource, searchSubmeters, getResources, getMeterSummary, getMeterDetail, getDeviceAllocationHistory, getMeterWorkorderInfo, updatePhysicalStatus, updateUnit, getActivities, getSubResources, generateMeterNumber, searchMasterMeters, getServiceOrdersJobCard } from "./api";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { queryClient } from "@shared/api/queries/queryClient";
import { useNavigate } from "react-router-dom";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";

export const useAddResource = () => {
  const { toast } = useToast();
  return useSmartMutation(
    (payload: any) => addResource(payload),
    {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Resource registered successfully.",
          variant: "default",
        });
        queryClient.refetchQueries({
          queryKey: ["mx", "resources-list"],
        });
        queryClient.refetchQueries({
          queryKey: ["mx", "meter-summary"],
        });
      },
    }
  );
};

export const useSearchSubmeters = (
  remoteUtilityId: string,
  search?: string,
  page?: number,
  limit?: number,
) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.searchSubmeters(
      remoteUtilityId,
      search,
      page,
      limit,
    ),
    () =>
      searchSubmeters({
        remoteUtilityId,
        search,
        page,
        limit,
      }),
    {
      enabled: !!remoteUtilityId,
    },
  );
};

export const useGetResources = (
  remoteUtilityId: string,
  searchData?: string,
  physicalStatusTab?: string,
  page?: number,
  limit?: number,
  utilityService?: string,
  resourceType?: string,
  resourceSubType?: string,
  status?: string,
) => {
  return useSmartQuery(
    ["mx", "resources-list", remoteUtilityId, searchData, physicalStatusTab, page, limit, utilityService, resourceType, resourceSubType, status],
    () =>
      getResources({
        remoteUtilityId,
        searchData,
        physicalStatusTab,
        page,
        limit,
        utilityService,
        resourceType,
        resourceSubType,
        status,
      }),
    {
      enabled: !!remoteUtilityId,
    },
  );
};

export const useMeterSummary = (remoteUtilityId: string) => {
  return useSmartQuery(
    ["mx", "meter-summary", remoteUtilityId],
    () => getMeterSummary(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
    },
  );
};

export const useMeterDetail = (meterId: string, remoteUtilityId?: string) => {
  return useSmartQuery(
    ["mx", "meter-detail", meterId, remoteUtilityId],
    () => getMeterDetail({ meterId, remoteUtilityId }),
    {
      enabled: !!meterId,
    },
  );
};

export const useDeviceAllocationHistory = ({
  meterNumber,
  deviceNo,
}: {
  meterNumber: any;
  deviceNo: any;
}) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    ["mx", "device-allocation-history", meterNumber, deviceNo, remoteUtilityId],
    () =>
      getDeviceAllocationHistory({
        meter_number: meterNumber,
        device_no: deviceNo,
        remoteUtilityId,
      }),
    {
      enabled: !!meterNumber && !!remoteUtilityId,
      staleTime: 0,
      refetchOnMount: true,
    },
  );
};

export const useMeterWorkorderInfo = ({
  meterId,
  remoteUtilityId,
}: {
  meterId: any;
  remoteUtilityId: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.device.meterWorkorderInfo(meterId),
    () =>
      getMeterWorkorderInfo({ meterId, remoteUtilityId }),
    {
      enabled: !!meterId && !!remoteUtilityId,
      refetchOnWindowFocus: false,
    },
  );
};

export const useUpdatePhysicalStatus = () => {
  const { toast } = useToast();
  return useSmartMutation(
    ({ resourceId, payload }: { resourceId: string; payload: any }) =>
      updatePhysicalStatus(resourceId, payload),
    {
      onSuccess: (data,variables: { resourceId: string; payload: any }) => {
        queryClient.refetchQueries({
          queryKey:["mx", "meter-detail", String(variables.resourceId),String(getRemoteUtilityId())],
        });
        toast({
          title: "Success",
          description: "Physical status updated successfully.",
          variant: "default",
        });
      },
    }
  );
};

export const useUpdateUnit = () => {
  const { toast } = useToast();
  return useSmartMutation(
    ({ resourceId, payload }: { resourceId: string; payload: { unit_code: string; premise: string } }) =>
      updateUnit(resourceId, payload),
    {
      onSuccess: (data,variables) => {
         queryClient.refetchQueries({
          queryKey:["mx", "meter-detail", String(variables.resourceId),String(getRemoteUtilityId())],
        });
        toast({
          title: "Success",
          description: "Unit updated successfully.",
          variant: "default",
        });
      },
    }
  );
};

export const useGetActivities = (resourceId: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.device.activities(resourceId),
    () => getActivities(resourceId),
    {
      enabled: !!resourceId,
    }
  );
};

export const useGetSubResources = ({ resourceId, search }: { resourceId: string; search?: string }) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.device.subResources(resourceId, search),
    () => getSubResources({ resourceId, search }),
    {
      enabled: !!resourceId,
    }
  );
};

export const useGenerateMeterNumber = (remoteUtilityId: string, enabled: boolean = false) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.generateMeterNumber(),
    () => generateMeterNumber(remoteUtilityId),
    {
      enabled,
    }
  );
};

export const useEditResource = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  return useSmartMutation(
    ({ resourceId, payload }: { resourceId: string; payload: any }) =>
      editResource(resourceId, payload),
    {
      onSuccess: (data: any, variables: any) => {
        toast({
          title: "Success",
          description: "Resource updated successfully.",
          variant: "default",
        });
        queryClient.refetchQueries({
          queryKey: ["mx", "resources-list"],
        });
        queryClient.refetchQueries({
          queryKey: ["mx", "meter-summary"],
        });
        queryClient.refetchQueries({
          queryKey: ["mx", "meter-detail", String(variables?.resourceId), String(getRemoteUtilityId())],
        });
        navigate("/mx/manage/resources");
      },
    }
  );
};

export const useGetMasterMeters = (remoteUtilityId: string) => {
  return useSmartQuery(
    ["mx", "master-meters-list", remoteUtilityId],
    () =>
      searchMasterMeters({
        remoteUtilityId,
      }),
    {
      enabled: !!remoteUtilityId,
    }
  );
};

export const useServiceOrdersJobCard = (
  remoteUtilityId: string,
  searchData?: string,
) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.serviceOrdersJobCard(remoteUtilityId, searchData),
    () =>
      getServiceOrdersJobCard({
        remoteUtilityId,
        searchData,
      }),
    {
      enabled: !!remoteUtilityId,
      staleTime: 0,
      refetchOnMount: false,
    },
  );
};

